// Champions for one scope (program / session / track / department): the list
// from GET /champions, "Add" from the audience-facets people picker (already
// narrowed to the caller's reach) with an optional label, and Remove. A
// champion can look at a session, roster and attendance but never marks.
import { useT } from '../lib/i18n';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { PersonPicker } from '@/features/shared/components/ConditionGroupBuilder';
import type { ChampionScopeKind } from '@/services/learningService';
import { useAddChampion, useAudienceFacets, useChampions, useRemoveChampion } from '../api/hooks';

interface Props {
  scopeKind: ChampionScopeKind;
  scopeId: string;
  /** Hide Add/Remove (learning:assign holders may list but not change). */
  readOnly?: boolean;
  className?: string;
  idPrefix?: string;
}

export const ChampionsEditor: React.FC<Props> = ({ scopeKind, scopeId, readOnly, className, idPrefix = 'ch' }) => {
  const t = useT();
  const q = useChampions(scopeKind, scopeId);
  const facets = useAudienceFacets(!readOnly);
  const add = useAddChampion();
  const remove = useRemoveChampion();
  const [adding, setAdding] = useState(false);
  const [userId, setUserId] = useState('');
  const [label, setLabel] = useState('');
  const champions = q.data || [];
  const taken = useMemo(() => new Set(champions.map((c) => c.user_id)), [champions]);
  const candidates = useMemo(() => (facets.data?.people || []).filter((p) => !taken.has(p.user_id)), [facets.data, taken]);

  const submit = () => add.mutate({ user_id: userId, scope_kind: scopeKind, scope_id: scopeId, label: label.trim() || undefined }, {
    onSuccess: () => { toast.success(t('training:champion_added', 'Champion added')); setAdding(false); setUserId(''); setLabel(''); },
  });

  return (
    <div className={`space-y-2 ${className || ''}`} data-testid="champions-editor">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" aria-hidden /> {t('training:champions', 'Champions')}</div>
        {!readOnly && !adding && <Button size="sm" variant="outline" onClick={() => setAdding(true)}><Plus className="mr-1 h-4 w-4" />{t('training:champion_add', 'Add champion')}</Button>}
      </div>
      <p className="text-[11px] text-muted-foreground">{t('training:champions_help', 'Champions are the go-to people for this {{scope}}: they see the roster and attendance and are listed under "Need help?" for participants.', { scope: t(`training:scope_${scopeKind}`, scopeKind) })}</p>
      {adding && (
        <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-500/10 p-2 space-y-2">
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-person`} className="text-xs">{t('training:champion', 'Champion')}</Label>
            {facets.isLoading && <div className="text-xs text-muted-foreground">{t('training:loading', 'Loading')}…</div>}
            <PersonPicker id={`${idPrefix}-person`} className="" multiple={false} people={candidates} value={userId ? [userId] : []}
              labels={{ searchPeople: t('training:search_people_ph', 'Type a name or department…'), noMatches: t('training:no_people_match', 'No one matches.'), selected: t('training:selected', 'Selected'), remove: t('training:remove', 'Remove') }}
              onChange={(ids) => setUserId(ids[0] || '')} />
            {facets.isSuccess && candidates.length === 0 && <p className="text-[11px] text-muted-foreground">{t('training:no_people_with_accounts', 'Only employees with a user account can be picked — none are linked yet.')}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-label`} className="text-xs">{t('training:champion_label', 'Label (optional)')}</Label>
            <Input id={`${idPrefix}-label`} className="h-8" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('training:champion_label_ph', 'e.g. Safety lead, Logistics contact')} />
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setUserId(''); setLabel(''); }}>{t('training:cancel', 'Cancel')}</Button>
            <Button size="sm" disabled={!userId || add.isPending} onClick={submit}>{add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:add_cert', 'Add')}</Button>
          </div>
        </div>
      )}
      {q.isLoading && <div className="text-xs text-muted-foreground">{t('training:loading', 'Loading')}…</div>}
      {!q.isLoading && champions.length === 0 && <p className="text-xs text-muted-foreground">{t('training:no_champions', 'No champions yet.')}</p>}
      {champions.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {champions.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-sm">
              <div className="min-w-0">
                <span className="text-foreground">{c.name}</span>
                {c.label && <span className="text-xs text-muted-foreground"> · {c.label}</span>}
              </div>
              {!readOnly && (
                <button type="button" aria-label={t('training:champion_remove', 'Remove {{name}}', { name: c.name })} className="text-muted-foreground hover:text-red-500 disabled:opacity-50" disabled={remove.isPending}
                  onClick={() => remove.mutate({ id: c.id, scopeKind, scopeId }, { onSuccess: () => toast.success(t('training:champion_removed', 'Champion removed')) })}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ChampionsEditor;
